import {
  useImperativeHandle,
  forwardRef,
  ForwardRefRenderFunction,
  Fragment,
} from 'react';
import { Transition } from '@headlessui/react';
import { XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';

import { H5 } from '@common/headline';
import { useNotifications } from '../Hooks/useNotifications';

export type StyleType = 'default' | 'success' | 'warning' | 'error';

type Notification = {
  title: string;
  message: string;
  style: StyleType;
  id: string;
  isVisible: boolean;
};

type Props = Record<string, unknown>;

type ConfigType = {
  showNotification: (title: string, message: string, style?: StyleType) => void;
};

/**
 * The notification can be used to show a notification at the top of the page
 *
 * It requires the component to be added within the tree - ideally at the highest level
 * e.g.:
 *
 * ```
 *   <Meta />
 *   <div className="page-container">
 *     ...
 *   </div>
 *   <Notification />
 * ```
 *
 * It is handled through a ref which can then be used to show a notification, e.g.:
 *
 * ```
 * const Notification = useRef(null);
 *
 * const onClick = () => {
 *   if (Notification.current) {
 *     Notification.current.showNotification('Button clicked!')
 *   }
 * }
 *
 * return (
 *   <button onClick={onClick} />
 *   <Notification ref={Notification} />
 * );
 * ```
 *
 * showNotification takes two parameters, message and style. Style defaults to 'default',
 * but can be 'success', 'warning', or 'error' - which show different colours of notification
 */

const Notification: ForwardRefRenderFunction<ConfigType, Props> = (
  props,
  ref,
) => {
  const { notificationsList, addNotification, hideNotification } =
    useNotifications();

  useImperativeHandle(ref, () => ({
    showNotification: addNotification,
  }));

  const getRingStyles = (style: StyleType) => {
    switch (style) {
      case 'success':
        return 'ring-green-400';
      case 'warning':
        return 'ring-yellow-400';
      case 'error':
        return 'ring-red-400';
      default:
        return 'ring-dawn-blue-400';
    }
  };

  const getTextStyles = (style: StyleType) => {
    switch (style) {
      case 'success':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-dawn-blue-400';
    }
  };

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-20 flex flex-col items-center justify-start px-4 py-6 sm:p-6"
    >
      {notificationsList.map(notification => (
        <Transition
          show={notification.isVisible}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          key={notification.id}
        >
          <div
            className={`pointer-events-auto mb-3 w-full max-w-sm overflow-hidden rounded-lg bg-mono-white shadow-lg ring-1 ${getRingStyles(
              notification.style,
            )}`}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="shrink-0">
                  <XCircleIcon
                    className={`h-5 w-5 ${getTextStyles(notification.style)}`}
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <H5>{notification.title}</H5>
                  <p className="mt-1 text-sm text-gray-500">
                    {notification.message}
                  </p>
                </div>
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    className={`focus:${getRingStyles(
                      notification.style,
                    )} inline-flex rounded-md bg-mono-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    onClick={() => hideNotification(notification.id)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      ))}
    </div>
  );
};

export default forwardRef(Notification);
